import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  TbAdjustmentsHorizontal, TbPlus, TbEdit, TbSearch, TbCheck, TbX,
  TbRobot, TbPuzzle, TbFilter, TbBuilding,
} from "react-icons/tb";
import aiChatbotsService, {
  ModuleCatalogItem, ModulesOverviewRow,
} from "../../features/ai_chatbots/aiChatbotsService";
import companiesService, { Company } from "../../features/companies/companiesService";
import BmsPagination from "../../components/bms/BmsPagination";
import { showSuccess, showError } from "../../utils/bmsToast";

const ModuleManagementPage: React.FC = () => {
  const { t } = useTranslation();

  const [catalog, setCatalog] = useState<ModuleCatalogItem[]>([]);
  const [rows, setRows] = useState<ModulesOverviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState<number | "">("");

  const [companies, setCompanies] = useState<Company[]>([]);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignCompanyId, setAssignCompanyId] = useState<number | "">("");
  const [assignChatbotId, setAssignChatbotId] = useState<number | null>(null);
  const [assignModuleKey, setAssignModuleKey] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignChatbots, setAssignChatbots] = useState<Array<{ id: number; name: string }>>([]);
  const [assignChatbotsLoading, setAssignChatbotsLoading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editRow, setEditRow] = useState<ModulesOverviewRow | null>(null);
  const [editState, setEditState] = useState<Record<string, boolean>>({});
  const [editSaving, setEditSaving] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await aiChatbotsService.getModulesCatalog();
      setCatalog(res || []);
    } catch { /* silent */ }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await companiesService.getCompanies({ limit: 200 });
      setCompanies(res.companies || []);
    } catch { /* silent */ }
  }, []);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await aiChatbotsService.getModulesOverview({
        skip, limit, search: search || undefined,
        module: moduleFilter || undefined,
        company_id: companyFilter || undefined,
        has_modules: true,
      });
      setRows(res.data || []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      showError(e?.response?.data?.detail || "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [skip, limit, search, moduleFilter, companyFilter]);

  useEffect(() => { loadCatalog(); loadCompanies(); }, [loadCatalog, loadCompanies]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setSkip(0); }, [search, moduleFilter, companyFilter]);

  const handleSearch = () => { setSearch(searchInput); };

  const openAssignModal = () => {
    setAssignCompanyId("");
    setAssignChatbots([]);
    setAssignChatbotId(null);
    setAssignModuleKey(enabledCatalog[0]?.module_key ?? "");
    setShowAssignModal(true);
  };

  const loadAssignChatbots = useCallback(async (cId: number | "") => {
    if (!cId) { setAssignChatbots([]); setAssignChatbotId(null); return; }
    setAssignChatbotsLoading(true);
    try {
      const res = await aiChatbotsService.getChatbots({ limit: 200, company_id: cId });
      const raw = res.chatbots || res.data || [];
      const list = raw.map((c: any) => ({ id: c.id, name: c.name }));
      setAssignChatbots(list);
      setAssignChatbotId(list[0]?.id ?? null);
    } catch { setAssignChatbots([]); setAssignChatbotId(null); }
    finally { setAssignChatbotsLoading(false); }
  }, []);

  const handleAssign = async () => {
    if (!assignChatbotId || !assignModuleKey) return;
    setAssignSaving(true);
    try {
      const currentModules = await aiChatbotsService.getChatbotModules(assignChatbotId);
      const modules = catalog.map((c) => {
        const current = currentModules.find((m) => m.module_key === c.module_key);
        return {
          module_key: c.module_key,
          enabled: c.module_key === assignModuleKey ? true : current?.status === "enabled",
        };
      });
      await aiChatbotsService.updateChatbotModules(assignChatbotId, modules);
      showSuccess(t("mod_assign_success"));
      setShowAssignModal(false);
      await loadRows();
    } catch (e: any) {
      showError(e?.response?.data?.detail || "Error");
    } finally {
      setAssignSaving(false);
    }
  };

  const openEditModal = async (row: ModulesOverviewRow) => {
    setEditRow(row);
    const state: Record<string, boolean> = {};
    catalog.forEach((c) => {
      const m = row.modules.find((mod) => mod.module_key === c.module_key);
      state[c.module_key] = m?.status === "enabled";
    });
    setEditState(state);
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    setEditSaving(true);
    try {
      const modules = catalog.map((c) => ({
        module_key: c.module_key,
        enabled: !!editState[c.module_key],
      }));
      await aiChatbotsService.updateChatbotModules(editRow.chatbot_id, modules);
      showSuccess(t("module_save_success"));
      setShowEditModal(false);
      setEditRow(null);
      await loadRows();
    } catch (e: any) {
      showError(e?.response?.data?.detail || "Error");
    } finally {
      setEditSaving(false);
    }
  };

  const enabledCatalog = useMemo(() => catalog.filter((c) => c.is_enabled_globally), [catalog]);

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2">
            <TbAdjustmentsHorizontal /> {t("mod_assignments")}
          </h1>
          <p>{t("mod_assignments_desc")}</p>
        </div>
        <button className="bms-btn-primary" onClick={openAssignModal}>
          <TbPlus /> {t("mod_assign_module")}
        </button>
      </div>

      <div className="p-3 pt-0">
        {/* Filters */}
        <div className="bms-section-card mb-3" style={{ padding: "0.75rem 1rem" }}>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="position-relative flex-grow-1" style={{ maxWidth: 300 }}>
              <TbSearch className="position-absolute top-50 translate-middle-y" style={{ left: 10, color: "#adb5bd" }} size={16} />
              <Form.Control
                size="sm" type="text" placeholder={t("mod_search_agent")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{ paddingLeft: 32 }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <TbFilter size={16} className="text-muted" />
              <Form.Select size="sm" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} style={{ width: 180 }}>
                <option value="">{t("mod_all_modules")}</option>
                {enabledCatalog.map((c) => (
                  <option key={c.module_key} value={c.module_key}>{c.name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="d-flex align-items-center gap-2">
              <TbBuilding size={16} className="text-muted" />
              <Form.Select
                size="sm"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value ? Number(e.target.value) : "")}
                style={{ width: 200 }}
              >
                <option value="">{t("mod_all_companies")}</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </Form.Select>
            </div>
            <button className="bms-btn-primary" onClick={handleSearch} style={{ fontSize: "0.8rem", padding: "5px 14px" }}>
              <TbSearch /> {t("search")}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bms-section-card">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="bms-empty" style={{ padding: "2rem" }}>
              <div className="bms-empty-icon"><TbRobot /></div>
              <p>{search || moduleFilter ? t("module_no_results") : t("bms_no_data")}</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="bms-table table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>ID</th>
                      <th>{t("mod_agent_name")}</th>
                      <th>{t("mod_company")}</th>
                      <th>{t("mod_enabled_modules")}</th>
                      <th style={{ width: 70 }}>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const enabled = r.modules.filter((m) => m.status === "enabled");
                      return (
                        <tr key={r.chatbot_id}>
                          <td className="text-muted">#{r.chatbot_id}</td>
                          <td className="fw-semibold">
                            <div className="d-flex align-items-center gap-2">
                              <TbRobot size={16} className="text-primary" />
                              {r.chatbot_name}
                            </div>
                          </td>
                          <td className="text-muted">{r.company_name || `#${r.company_id}`}</td>
                          <td>
                            {enabled.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {enabled.map((m) => (
                                  <Badge bg="success" key={m.module_key} className="fw-normal" style={{ fontSize: "0.75rem" }}>
                                    <TbPuzzle size={12} className="me-1" />{m.module_key}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>{t("module_none")}</span>
                            )}
                          </td>
                          <td>
                            <button className="bms-action-btn edit" onClick={() => openEditModal(r)} title={t("edit")}>
                              <TbEdit />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <BmsPagination total={total} skip={skip} limit={limit} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
            </>
          )}
        </div>
      </div>

      {/* Assign Module Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} className="bms-modal" centered>
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2"><TbPlus /> {t("mod_assign_module")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="bms-form-label"><TbBuilding size={14} className="me-1" />{t("mod_select_company")} *</Form.Label>
            <Form.Select
              value={assignCompanyId}
              onChange={(e) => {
                const v = e.target.value ? Number(e.target.value) : "";
                setAssignCompanyId(v);
                loadAssignChatbots(v);
              }}
            >
              <option value="">{t("mod_select_company_placeholder")}</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="bms-form-label"><TbRobot size={14} className="me-1" />{t("mod_select_agent")} *</Form.Label>
            {assignChatbotsLoading ? (
              <div className="d-flex align-items-center gap-2 py-2 text-muted"><Spinner size="sm" animation="border" /> {t("loading")}...</div>
            ) : !assignCompanyId ? (
              <Form.Select disabled><option>{t("mod_select_company_first")}</option></Form.Select>
            ) : assignChatbots.length === 0 ? (
              <Form.Select disabled><option>{t("mod_no_agents_for_company")}</option></Form.Select>
            ) : (
              <Form.Select value={assignChatbotId ?? ""} onChange={(e) => setAssignChatbotId(Number(e.target.value))}>
                {assignChatbots.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="bms-form-label"><TbPuzzle size={14} className="me-1" />{t("mod_select_module")} *</Form.Label>
            <Form.Select value={assignModuleKey} onChange={(e) => setAssignModuleKey(e.target.value)}>
              {enabledCatalog.map((c) => (
                <option key={c.module_key} value={c.module_key}>{c.name} ({c.module_key})</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => setShowAssignModal(false)}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleAssign} disabled={assignSaving || !assignChatbotId || !assignModuleKey}>
            {assignSaving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("mod_assign_module")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modules Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} className="bms-modal" centered size="lg">
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2">
            <TbEdit /> {t("mod_edit_modules")} — {editRow?.chatbot_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRow && (
            <div className="d-flex flex-column gap-2">
              {catalog.map((item) => (
                <div
                  key={item.module_key}
                  className={`d-flex justify-content-between align-items-center border rounded p-3 ${
                    editState[item.module_key] ? "border-success bg-success bg-opacity-10" : ""
                  }`}
                  style={{ transition: "all 0.2s" }}
                >
                  <div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <TbPuzzle size={16} />
                      {item.name}
                      <Badge bg="light" text="dark" style={{ fontSize: "0.7rem", fontWeight: 400 }}>{item.module_key}</Badge>
                    </div>
                    {item.description && <small className="text-muted">{item.description}</small>}
                  </div>
                  <Form.Check
                    type="switch"
                    id={`edit-module-${item.module_key}`}
                    checked={!!editState[item.module_key]}
                    disabled={!item.is_enabled_globally}
                    onChange={(e) => setEditState((prev) => ({ ...prev, [item.module_key]: e.target.checked }))}
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <small className="text-muted me-auto">
            {Object.values(editState).filter(Boolean).length} / {catalog.length} {t("module_active_count")}
          </small>
          <button className="bms-btn-secondary" onClick={() => setShowEditModal(false)}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ModuleManagementPage;
